import { z } from 'zod';

import type {
  ShipmentReportedBoundaryValidation,
  ShipmentRangeHistoricalGap,
  ShipmentRangeOverlap,
  ValidateShipmentRangeRequest,
  ValidateShipmentRangeResponse,
} from './validate-shipment-range.dto.js';
import {
  shipmentRangeValidationBlockingReasonValues,
  shipmentRangeValidationWarningValues,
} from './validate-shipment-range.dto.js';
import { IsoDateTimeSchema } from './shipments-types.js';

const ShipmentRangeOverlapSchema: z.ZodType<ShipmentRangeOverlap> = z
  .object({
    hasOverlap: z.boolean(),
    severity: z.enum(['none', 'blocker']),
    overlappedPurchaseCommittedIds: z.array(z.uuid()),
    reservedOverlapCount: z.number().int().min(0),
    departedOverlapCount: z.number().int().min(0),
    recommendedSafeStartAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

const ShipmentRangeHistoricalGapSchema: z.ZodType<ShipmentRangeHistoricalGap> = z
  .object({
    hasHistoricalGap: z.boolean(),
    severity: z.enum(['none', 'warning']),
    earliestHistoricalUnshippedAt: IsoDateTimeSchema.nullable(),
    recommendedStartAt: IsoDateTimeSchema.nullable(),
  })
  .strict();

const ShipmentReportedBoundaryValidationSchema: z.ZodType<ShipmentReportedBoundaryValidation> = z
  .object({
    hasConflict: z.boolean(),
    severity: z.enum(['none', 'blocker']),
    materialId: z.uuid(),
    lastReportedEndAt: IsoDateTimeSchema.nullable(),
    message: z.string().trim().min(1).nullable(),
  })
  .strict();

const validateShipmentRangeBaseSchema = z
  .object({
    companyId: z.uuid(),
    rcScopeId: z.uuid(),
    materialId: z.uuid(),
    shipmentId: z.uuid().optional(),
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
  })
  .strict();

export const ValidateShipmentRangeRequestSchema: z.ZodType<ValidateShipmentRangeRequest> =
  validateShipmentRangeBaseSchema.superRefine((value, ctx) => {
    const rangeStartMs = Date.parse(value.rangeStartAt);
    const rangeEndMs = Date.parse(value.rangeEndAt);
    if (!Number.isNaN(rangeStartMs) && !Number.isNaN(rangeEndMs) && rangeStartMs >= rangeEndMs) {
      ctx.addIssue({
        code: 'custom',
        message: 'rangeStartAt (inclusive) must be before rangeEndAt (exclusive).',
        path: ['rangeEndAt'],
      });
    }
  });

export const ValidateShipmentRangeResponseSchema: z.ZodType<ValidateShipmentRangeResponse> = z
  .object({
    isValid: z.boolean(),
    canSubmit: z.boolean(),
    overlap: ShipmentRangeOverlapSchema,
    historicalGap: ShipmentRangeHistoricalGapSchema,
    reportedBoundary: ShipmentReportedBoundaryValidationSchema,
    blockingReasons: z.array(z.enum(shipmentRangeValidationBlockingReasonValues)),
    warnings: z.array(z.enum(shipmentRangeValidationWarningValues)),
    notes: z.array(z.string().trim().min(1)).optional(),
  })
  .strict();
